import { Component, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ProblemDetails, TenantNotificationItemsResult, TenantNotificationSeverity } from '@api';
import { map, Observable } from 'rxjs';
import { tryParseJSON } from '../../extensions/string.extensions';
import { ApplicationState } from '../../state/application.state';
import { GetNotifications } from '../../state/notifications-state/get-notifications/get-notifications.actions';
import { NotificationsSelectors } from '../../state/notifications-state/notifications.selectors';
import { PushNotifications } from '../../state/notifications-state/push-notifications/push-notifications.actions';
import { ReadAllNotifications } from '../../state/notifications-state/read-all-notifications/read-all-notifications.actions';
import { ReadNotification } from '../../state/notifications-state/read-notification/read-notification.actions';
import { ComponentBaseDirective, IAllowRealTimeUpdates } from '../component.base';
import { IFilterCriterias } from './notifications-filter/filter-criterias.interface';
import { notificationTypes } from './notifications.repository';

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    standalone: false,
})
export class NotificationsComponent extends ComponentBaseDirective implements OnInit, IAllowRealTimeUpdates {
    private readonly _messageParametersRegex = /\{\{\s*([^\}\s]+)\s*\}\}/gi;
    private tenantName: string;
    public isFilteringEnabled = false;
    public TenantNotificationSeverity = TenantNotificationSeverity;
    public filterCriterias = { notificationOwner: null, notificationSeverity: null, notificationType: null } as IFilterCriterias;

    public notifications$: Observable<TenantNotificationItemsResult> = this.store.select(NotificationsSelectors.notifications);
    private currentUserId = toSignal(this.store.select(ApplicationState.currentUser).pipe(map((user) => user?.localAccountId)));

    constructor() {
        super();
    }

    public ngOnInit() {
        this.tenantName = this.store.selectSnapshot(ApplicationState.tenantName);

        this.addLoader('notifications-loader', GetNotifications, ReadNotification, ReadAllNotifications);

        this.ofActionSuccessful(() => this.store.dispatch(new GetNotifications()), ReadNotification);

        this.ofActionDispatched((action: PushNotifications) => this.pushNotifications(action), PushNotifications);

        this.store.dispatch(new GetNotifications());

        this.startRealTimeUpdates();
    }

    public getNotificationTypeTitle(notificationType: keyof typeof notificationTypes, messageParameters: string) {
        const title = this.translationService.translate(notificationTypes[notificationType]?.title, tryParseJSON(messageParameters));
        const unknown = this.translationService.translate('defaults.unknown');

        return title ?? unknown;
    }

    public getNotificationTypeText(notificationType: keyof typeof notificationTypes, messageParameters: string) {
        const text = this.translationService.translate(notificationTypes[notificationType]?.text, tryParseJSON(messageParameters));
        const unknown = this.translationService.translate('defaults.unknown');

        return text ?? unknown;
    }

    public readNotification(notificationId: string) {
        this.store.dispatch(new ReadNotification(notificationId));
    }

    public markAllAsRead() {
        this.store.dispatch(new ReadAllNotifications());
    }

    public filterNotifications(values: IFilterCriterias) {
        this.filterCriterias = values;
        this.store.dispatch(new GetNotifications(values.notificationOwner, values.notificationSeverity, values.notificationType));
    }

    public toggleFilterContainer() {
        this.isFilteringEnabled = !this.isFilteringEnabled;
        if (!this.isFilteringEnabled) {
            this.filterCriterias = null;
            this.store.dispatch(new GetNotifications());
        }
    }

    public changePage(page: number) {
        this.store.dispatch(new GetNotifications(this.filterCriterias.notificationOwner, this.filterCriterias.notificationSeverity, this.filterCriterias.notificationType, page));
    }

    public async startRealTimeUpdates() {
        await this.startRealTime(this.tenantName, 'Notifications', PushNotifications);
    }

    public async stopRealTimeUpdates() {
        await this.stopRealTime(this.tenantName, 'Notifications', PushNotifications);
    }

    private pushNotifications(action: PushNotifications) {
        for (const notification of action.notifications) {
            if (this.currentUserId() === this.getUserIdFromMessageParameters(notification.messageParameters)) {
                continue;
            }

            const notificationType = notification.notificationType as keyof typeof notificationTypes;
            const title = this.getNotificationTypeTitle(notificationType, notification.messageParameters);
            const text = this.getNotificationTypeText(notificationType, notification.messageParameters);

            if (notification.severity === TenantNotificationSeverity.NUMBER_0) {
                this.notificationsService.info(text, title);
            } else if (notification.severity === TenantNotificationSeverity.NUMBER_1) {
                this.notificationsService.warning(text, title);
            } else if (notification.severity === TenantNotificationSeverity.NUMBER_2) {
                this.notificationsService.error(text, { title } as ProblemDetails);
            }
        }
    }

    private getUserIdFromMessageParameters(messageParameters: string): string | null {
        const userId = tryParseJSON(messageParameters, {})?.userId;
        return userId;
    }
}
